import { formatAUTime } from "@/lib/utils";

interface Props {
  postcode: string;
  fetchedAt: string;
  cacheHit: boolean;
  /**
   * pSEO SWR (2026-06-11): true when the cached row is older than the
   * freshness target (24h). The page still renders the cached data
   * instantly; the backend has already fired a background refresh so
   * the next visitor sees fresh data. Disclosure wording shifts to
   * "Refreshing now" so the user understands the prices are not
   * being checked in real time on this particular visit.
   */
  isStale?: boolean;
  /**
   * 2026-08-02: which price the page shows. "tfp" (default, the /c comparison
   * pages) mentions the True Final Price. "listed" (broad /c/list + inline
   * FastResults pages) shows the lowest LISTED price and must NEVER say
   * "True Final Price".
   */
  priceBasis?: "tfp" | "listed";
}

/**
 * Page-bottom notes. Per Section 5.10 legal posture: transparency about
 * what Mio is, what it does, and what's estimated. No marketing copy.
 */
export function Disclosures({
  postcode,
  fetchedAt,
  cacheHit,
  isStale,
  priceBasis = "tfp",
}: Props) {
  // F2 (2026-05-13): explicitly format in Australia/Sydney; without
  // an explicit timeZone the SSR runtime (DO App Platform UTC) and
  // browser runtime (user's local zone) disagree, and the user-
  // facing timestamp was rendering as UTC on prod.
  const fetchedDisplay = formatAUTime(fetchedAt);
  // Postcode hidden from the notes 2026-07-27 (until per-area delivery,
  // B-131); the prop stays for a one-line re-enable.
  void postcode;
  // 2026-07-31: freshness is shown ONLY as the plain "Updated <time>" line
  // below. The previous SWR/cache mechanics wording ("Refreshing now (next
  // visitor sees the new prices)" and "Served from cache (refreshes every 12
  // hours)") was removed - internal refresh plumbing must never surface in
  // the user-facing notes. Behaviour (serve-then-refresh) is unchanged; only
  // the copy is dropped. Props kept for API stability.
  void isStale;
  void cacheHit;

  // 2026-05-31 mockup audit: notes section vertical rhythm + typography
  // brought back to locked-mockup values.
  //   .notes-section  margin-top 22px (mobile) / 32px (desktop);
  //                   padding-top 18px (mobile) / 22px (desktop);
  //                   margin-bottom 28px.
  //   .notes-eyebrow  10px / weight 600 / uppercase / tracking 0.12em /
  //                   color var(--fg-3) / mb 8px.
  //   .notes-list     11px (mobile) / 13px (desktop); line-height
  //                   1.6 / 1.65; inter-item gap 6px (mobile) / 8px
  //                   (desktop).
  return (
    <section className="mt-[22px] border-t border-border pt-[18px] mb-7 text-muted-foreground sm:mt-8 sm:pt-[22px]">
      <p className="mb-2 text-[12px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        Notes
      </p>
      {/* 2026-10-01 (owner's words and option B of the mockup): the AI notice,
          highlighted in a soft panel above the other notes. Theme tokens only
          (muted fill, hairline border, ink text), so it follows light and dark. */}
      <div className="mb-3 mt-1 flex items-start gap-2.5 rounded-xl border border-border bg-muted px-3.5 py-3 text-sm font-semibold leading-[1.55] text-foreground">
        <svg
          aria-hidden="true"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          className="mt-px flex-shrink-0"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <p>
          Octopusden is AI and can make mistakes. Always confirm the final
          price with the retailer before you buy.
        </p>
      </div>
      <ul className="space-y-1.5 text-sm leading-[1.6] sm:space-y-2 sm:leading-[1.65]">
        <li>
          Prices and availability can change. Updated {fetchedDisplay}.
        </li>
        <li>
          {priceBasis === "listed" ? (
            <>
              Prices shown are the lowest listed price we found. The retailer
              confirms the final delivered amount when you buy.
            </>
          ) : (
            <>
              The True Final Price is our estimate of what you&apos;ll actually
              pay, including delivery and any savings that apply. The retailer
              confirms the final amount.
            </>
          )}
        </li>
        <li>
          Octopusden is independent. We compare prices only. We don&apos;t
          sell, take payment or earn commission, and we&apos;re not affiliated
          with or endorsed by the retailers shown.
        </li>
      </ul>
    </section>
  );
}
